import { supabase } from './supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { generateRandomName } from '../utils/nameGenerator';
import type { PeerData } from '../types';

/**
 * Signaling service for automatic WebRTC connection establishment
 * Uses Supabase Realtime channels for peer discovery and signal exchange
 */
export class SignalingService {
  private channel: RealtimeChannel | null = null;
  private peerId: string;
  private peerName: string;
  private isInitiator: boolean = false;
  private peerCount: number = 0;
  private roleAssigned: boolean = false;
  private remotePeerData: PeerData | null = null;

  // Callbacks
  private onOfferReceived?: (offer: RTCSessionDescriptionInit) => void;
  private onAnswerReceived?: (answer: RTCSessionDescriptionInit) => void;
  private onPeerJoined?: (remotePeer: PeerData) => void;
  private onPeerLeft?: () => void;

  constructor() {
    this.peerId = this.generatePeerId();
    this.peerName = generateRandomName();
    console.log('🎲 Generated peer name:', this.peerName);
  }

  /**
   * Generate unique peer ID
   */
  private generatePeerId(): string {
    return `peer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Join a space and start listening for signals
   */
  async joinSpace(spaceId: string): Promise<void> {
    // Leave existing channel if any
    if (this.channel) {
      await this.leaveSpace();
    }

    console.log('🔌 Joining space:', spaceId);

    // Create channel for this space
    this.channel = supabase.channel(`space:${spaceId}`, {
      config: {
        presence: {
          key: this.peerId,
        },
      },
    });

    // Track presence - who's in the space
    this.channel
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel?.presenceState();
        const peers = state ? Object.keys(state) : [];
        this.peerCount = peers.length;

        console.log('👥 Peers in space:', this.peerCount, peers);

        // Determine roles when we have 2 peers
        if (peers.length === 1) {
          this.isInitiator = false;
          this.roleAssigned = false;
          this.remotePeerData = null;
          console.log('⏳ Waiting for peer to join...');
        } else if (peers.length === 2 && !this.roleAssigned) {
          // Sort peer IDs lexicographically for deterministic role assignment
          // The peer with the LARGER peer ID becomes the initiator (second to join typically has larger timestamp)
          const sortedPeers = peers.sort();
          this.isInitiator = this.peerId === sortedPeers[1]; // Second in sorted order = initiator
          this.roleAssigned = true;
          
          // Get remote peer data
          const remotePeerId = peers.find(id => id !== this.peerId);
          if (remotePeerId && state) {
            const remotePeerPresence = state[remotePeerId];
            const remoteData = remotePeerPresence?.[0] as any;
            this.remotePeerData = {
              id: remotePeerId,
              name: remoteData?.peer_name || 'Unknown Peer',
              connected: false
            };
            console.log('🤝 Remote peer:', this.remotePeerData.name);
          }
          
          console.log('🎯 My peer ID:', this.peerId);
          console.log('🎯 Sorted peers:', sortedPeers);
          console.log('🎯 Role:', this.isInitiator ? 'Initiator' : 'Responder');
          
          // Small delay to ensure both peers have settled
          setTimeout(() => {
            if (this.remotePeerData) {
              this.onPeerJoined?.(this.remotePeerData);
            }
          }, 100);
        }
      })
      .on('presence', { event: 'leave' }, () => {
        console.log('👋 Peer left');
        this.peerCount--;
        this.remotePeerData = null;
        this.onPeerLeft?.();
      });

    // Listen for WebRTC signals
    this.channel
      .on('broadcast', { event: 'offer' }, ({ payload }) => {
        if (payload.from !== this.peerId) {
          console.log('📥 Received offer');
          this.onOfferReceived?.(payload.offer);
        }
      })
      .on('broadcast', { event: 'answer' }, ({ payload }) => {
        if (payload.from !== this.peerId) {
          console.log('📥 Received answer');
          this.onAnswerReceived?.(payload.answer);
        }
      });

    // Subscribe and track presence
    await this.channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await this.channel?.track({
          peer_id: this.peerId,
          peer_name: this.peerName,
          joined_at: new Date().toISOString(),
        });
        console.log('✅ Connected to signaling channel as:', this.peerName);
      }
    });
  }

  /**
   * Send offer to peer
   */
  async sendOffer(offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.channel) {
      throw new Error('Not connected to signaling channel');
    }

    console.log('📤 Sending offer');
    await this.channel.send({
      type: 'broadcast',
      event: 'offer',
      payload: {
        from: this.peerId,
        offer,
      },
    });
  }

  /**
   * Send answer to peer
   */
  async sendAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.channel) {
      throw new Error('Not connected to signaling channel');
    }

    console.log('📤 Sending answer');
    await this.channel.send({
      type: 'broadcast',
      event: 'answer',
      payload: {
        from: this.peerId,
        answer,
      },
    });
  }

  /**
   * Update peer name locally without broadcasting (used for initial setup)
   */
  updatePeerNameLocally(newName: string): void {
    this.peerName = newName;
    console.log('✏️ Set local peer name to:', newName);
  }

  /**
   * Update peer name and broadcast to channel
   */
  async updatePeerName(newName: string): Promise<void> {
    this.peerName = newName;
    console.log('✏️ Updated peer name to:', newName);
    
    // Update presence with new name
    if (this.channel) {
      await this.channel.track({
        peer_id: this.peerId,
        peer_name: this.peerName,
        joined_at: new Date().toISOString(),
      });
    }
  }

  /**
   * Leave the current space
   */
  async leaveSpace(): Promise<void> {
    if (this.channel) {
      try {
        await this.channel.untrack();
        await this.channel.unsubscribe();
      } catch (err) {
        console.warn('Error during channel cleanup:', err);
      }
      this.channel = null;
    }
    this.isInitiator = false;
    this.roleAssigned = false;
    this.peerCount = 0;
    this.remotePeerData = null;
    console.log('👋 Left space');
  }

  /**
   * Set callback handlers
   */
  setCallbacks(callbacks: {
    onOfferReceived?: (offer: RTCSessionDescriptionInit) => void;
    onAnswerReceived?: (answer: RTCSessionDescriptionInit) => void;
    onPeerJoined?: (remotePeer: PeerData) => void;
    onPeerLeft?: () => void;
  }): void {
    this.onOfferReceived = callbacks.onOfferReceived;
    this.onAnswerReceived = callbacks.onAnswerReceived;
    this.onPeerJoined = callbacks.onPeerJoined;
    this.onPeerLeft = callbacks.onPeerLeft;
  }

  /**
   * Get current role
   */
  getIsInitiator(): boolean {
    return this.isInitiator;
  }

  /**
   * Get peer count
   */
  getPeerCount(): number {
    return this.peerCount;
  }

  /**
   * Get peer ID
   */
  getPeerId(): string {
    return this.peerId;
  }

  /**
   * Get local peer name
   */
  getPeerName(): string {
    return this.peerName;
  }

  /**
   * Get remote peer data
   */
  getRemotePeerData(): PeerData | null {
    return this.remotePeerData;
  }
}

export const signalingService = new SignalingService();
